import React from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import {
    ExclamationTriangleIcon,
    TrashIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';

interface DeleteConfirmDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: string;
    confirmButtonText?: string;
    isDestructive?: boolean;
    isLoading?: boolean;
}

export default function DeleteConfirmDialog({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmButtonText = 'Delete',
    isDestructive = true,
    isLoading = false,
}: DeleteConfirmDialogProps) {
    return (
        <Dialog open={isOpen} onClose={onClose} className="relative z-50">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />

            {/* Dialog container */}
            <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
                <DialogPanel className="relative max-w-md w-full bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
                    {/* Close button */}
                    <button
                        onClick={onClose}
                        disabled={isLoading}
                        className="absolute top-4 right-4 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>

                    {/* Content */}
                    <div className="p-6 pt-8">
                        {/* Icon and title */}
                        <div className="flex flex-col items-center text-center mb-6">
                            <div
                                className={`flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                                    isDestructive
                                        ? 'bg-red-100'
                                        : 'bg-amber-100'
                                }`}
                            >
                                {isDestructive ? (
                                    <TrashIcon className="h-8 w-8 text-red-600" />
                                ) : (
                                    <ExclamationTriangleIcon className="h-8 w-8 text-amber-600" />
                                )}
                            </div>
                            <DialogTitle className="text-xl font-semibold text-gray-900 mb-2">
                                {title}
                            </DialogTitle>
                            <p className="text-sm text-gray-600 leading-relaxed">
                                {message}
                            </p>
                        </div>

                        {/* Action buttons */}
                        <div className="space-y-3">
                            {/* Primary action - Confirm Delete */}
                            <button
                                onClick={onConfirm}
                                disabled={isLoading}
                                className={`w-full flex items-center justify-center gap-2 px-4 py-3 text-white rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl ${
                                    isDestructive
                                        ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500 disabled:bg-red-400'
                                        : 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500 disabled:bg-amber-400'
                                }`}
                                style={{
                                    width: 'calc(100% - 2rem)',
                                }}
                            >
                                {isLoading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                        <span>Processing...</span>
                                    </>
                                ) : (
                                    <>
                                        <TrashIcon className="h-5 w-5" />
                                        <span>{confirmButtonText}</span>
                                    </>
                                )}
                            </button>

                            {/* Cancel button */}
                            <button
                                onClick={onClose}
                                disabled={isLoading}
                                className="w-full px-4 text-center py-3 bg-gray-50 text-gray-700 rounded-xl font-medium hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                                style={{
                                    width: 'calc(100% - 2rem)',
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </DialogPanel>
            </div>
        </Dialog>
    );
}
